import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImagePlus, X } from "lucide-react";
import { api, getDisplayProofTypes } from "../../services/api";

const MAX_FILES = 8;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const ACCEPTED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
]);

const createPreviewItem = (file) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  file,
  previewUrl: URL.createObjectURL(file),
});

export default function DisplayProofForm({ activeCheckIn, onCancel, onSave }) {
  const { t } = useTranslation();
  const [proofType, setProofType] = useState("");
  const [proofTypes, setProofTypes] = useState([]);
  const [loadingProofTypes, setLoadingProofTypes] = useState(true);
  const [notes, setNotes] = useState("");
  const [images, setImages] = useState([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const outletName = useMemo(() => {
    if (!activeCheckIn) return "-";
    return (
      activeCheckIn.outlet_name ||
      activeCheckIn.outlet?.bp_outlet_name ||
      t("se.displayProof.unknownOutlet")
    );
  }, [activeCheckIn, t]);

  useEffect(() => {
    return () => {
      images.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
  }, [images]);

  useEffect(() => {
    let isCancelled = false;

    const fetchProofTypes = async () => {
      try {
        setLoadingProofTypes(true);
        const response = await getDisplayProofTypes();
        if (isCancelled) return;
        const nextTypes = Array.isArray(response?.data) ? response.data : [];
        setProofTypes(nextTypes);
        setProofType((previous) => {
          if (previous && nextTypes.some((item) => item.code === previous)) {
            return previous;
          }
          return nextTypes[0]?.code || "";
        });
      } catch (requestError) {
        if (isCancelled) return;
        setProofTypes([]);
        setProofType("");
        setError(
          requestError?.response?.data?.error ||
            requestError?.response?.data?.message ||
            t("se.displayProof.errors.failedToLoadTypes"),
        );
      } finally {
        if (!isCancelled) {
          setLoadingProofTypes(false);
        }
      }
    };

    fetchProofTypes();
    return () => {
      isCancelled = true;
    };
  }, [t]);

  const validateFiles = (selectedFiles) => {
    const validFiles = [];
    for (const file of selectedFiles) {
      if (!ACCEPTED_IMAGE_TYPES.has(file.type)) {
        setError(t("se.displayProof.errors.invalidType"));
        continue;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setError(t("se.displayProof.errors.fileTooLarge"));
        continue;
      }
      validFiles.push(file);
    }
    return validFiles;
  };

  const handleFileChange = (event) => {
    setError("");
    const fileList = Array.from(event.target.files || []);
    if (!fileList.length) return;

    const remainingSlots = MAX_FILES - images.length;
    if (remainingSlots <= 0) {
      setError(t("se.displayProof.errors.maxFiles"));
      return;
    }

    const nextFiles = validateFiles(fileList.slice(0, remainingSlots));
    if (!nextFiles.length) return;

    const previewItems = nextFiles.map(createPreviewItem);
    setImages((prev) => [...prev, ...previewItems]);

    event.target.value = "";
  };

  const handleRemoveImage = (id) => {
    setImages((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((item) => item.id !== id);
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!activeCheckIn?.check_in_id) {
      setError(
        t("se.displayProof.errors.outletRequired", {
          defaultValue: "Select an outlet before submitting display proof.",
        }),
      );
      return;
    }

    if (!proofType) {
      setError(t("se.displayProof.errors.proofTypeRequired"));
      return;
    }

    if (proofType === "others" && !notes.trim()) {
      setError(t("se.displayProof.errors.notesRequiredForOthers"));
      return;
    }

    if (!images.length) {
      setError(t("se.displayProof.errors.imagesRequired"));
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append("check_in_id", String(activeCheckIn.check_in_id));
      formData.append("proof_type", proofType);
      if (notes.trim()) {
        formData.append("notes", notes.trim());
      }

      images.forEach((item) => {
        formData.append("photos", item.file);
      });

      const response = await api.post("/display-proofs", formData);
      if (!response?.data?.success) {
        throw new Error(t("se.displayProof.errors.submitFailed"));
      }

      onSave?.(response.data.data);
    } catch (requestError) {
      const apiMessage =
        requestError?.response?.data?.error ||
        requestError?.response?.data?.message ||
        requestError?.message ||
        t("se.displayProof.errors.submitFailed");
      setError(apiMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="se-display-proof-container">
      <form className="se-display-proof-form" onSubmit={handleSubmit}>
        <p className="se-display-proof-subtitle">
          {t("se.displayProof.subtitle")}
        </p>

        <div className="form-group">
          <label>{t("se.displayProof.outletLabel")}</label>
          <input type="text" value={outletName} disabled readOnly />
        </div>

        <div className="form-group">
          <label>
            {t("se.displayProof.proofTypeLabel")}
            <span className="required-asterisk" aria-hidden="true">*</span>
          </label>
          <select
            value={proofType}
            onChange={(event) => setProofType(event.target.value)}
            disabled={submitting || loadingProofTypes}
          >
            {loadingProofTypes ? (
              <option value="">{t("admin.common.loading")}</option>
            ) : proofTypes.length > 0 ? (
              proofTypes.map((type) => (
                <option key={type.proof_type_id || type.code} value={type.code}>
                  {type.name}
                </option>
              ))
            ) : (
              <option value="">{t("se.displayProof.errors.noTypesAvailable")}</option>
            )}
          </select>
        </div>

        <div className="form-group">
          <label>{t("se.displayProof.notesLabel")}</label>
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder={t("se.displayProof.notesPlaceholder")}
            rows={3}
            maxLength={3000}
            disabled={submitting}
          />
        </div>

        <div className="form-group">
          <label>
            {t("se.displayProof.photosLabel")}
            <span className="required-asterisk" aria-hidden="true">*</span>
          </label>
          <label className="se-display-proof-upload-trigger">
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              multiple
              onChange={handleFileChange}
              disabled={submitting || images.length >= MAX_FILES}
              style={{ display: "none" }}
            />
            <div className="se-display-proof-upload-box">
              <ImagePlus size={20} />
              <span>{t("se.displayProof.addPhotos")}</span>
              <small>
                {t("se.displayProof.photoHint", { count: MAX_FILES })}
              </small>
            </div>
          </label>

          {images.length > 0 && (
            <div className="se-display-proof-image-grid">
              {images.map((item) => (
                <div key={item.id} className="se-display-proof-image-item">
                  <img src={item.previewUrl} alt="Display proof preview" />
                  <button
                    type="button"
                    className="se-display-proof-remove-image"
                    onClick={() => handleRemoveImage(item.id)}
                    disabled={submitting}
                    aria-label={t("se.displayProof.removePhoto")}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {error ? <div className="form-error">{error}</div> : null}

        <div className="form-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onCancel}
            disabled={submitting}
          >
            {t("se.displayProof.cancel")}
          </button>
          <button type="submit" className="btn-submit" disabled={submitting}>
            {submitting
              ? t("se.displayProof.submitting")
              : t("se.displayProof.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}
